import React, { useMemo, useRef, useState } from 'react';
import { TimePicker } from 'antd';
import { Moment } from 'moment';
import { I18n } from '@aws-amplify/core';

import { ClockIcon, MinusIcon } from '@/assets/icons/v2';
import type { CycleCallback } from '@/components/ui/Timeline';
import { dayjsToSeconds } from '@/components/ui/Timeline/utils/time-conversions';
import {
	buildPickerBounds,
	getDisplayedDurationInSeconds,
	resolvePickerPlacement,
	toDayjs,
	toMoment,
	type PickerBounds,
	type PickerPlacement
} from '../helpers';
import {
	RemoveStretchButton,
	StretchArrow,
	StretchDuration,
	StretchFileName,
	StretchIndex,
	StretchInterval,
	StretchRow
} from '../styles';

const TIME_FORMAT = 'HH:mm:ss';
const FIRST_MILLISECOND = 0;
const LAST_MILLISECOND = 999;

interface ItemProps {
	position: number;
	videoDuration: number;
	fileName: string;
	stretch: CycleCallback;
	onChange: (stretch: CycleCallback) => void;
	onRemove: (stretchId: string) => void;
}

export function Item({
	position,
	videoDuration,
	fileName,
	stretch,
	onChange,
	onRemove
}: Readonly<ItemProps>): JSX.Element {
	const duration = getDisplayedDurationInSeconds(stretch);

	const pickerBounds = buildPickerBounds(videoDuration);

	const startTimestamp = stretch.start_time.valueOf();
	const endTimestamp = stretch.end_time.valueOf();

	const startValue = useMemo(() => toMoment(stretch.start_time), [startTimestamp]);
	const endValue = useMemo(() => toMoment(stretch.end_time), [endTimestamp]);

	const rowRef = useRef<HTMLDivElement>(null);
	const [placement, setPlacement] = useState<PickerPlacement>('bottomLeft');

	function handleOpenChange(open: boolean): void {
		const row = rowRef.current;

		if (!open || !row) {
			return;
		}

		const rect = row.getBoundingClientRect();
		const params = { anchorTop: rect.top, anchorBottom: rect.bottom, viewportHeight: window.innerHeight };

		setPlacement(resolvePickerPlacement(params));
	}

	function getPickerBounds(): PickerBounds {
		return pickerBounds;
	}

	function handleStartChange(value: Moment | null): void {
		if (!value) {
			return;
		}

		const start_time = toDayjs(value).millisecond(FIRST_MILLISECOND);

		if (dayjsToSeconds(start_time) >= dayjsToSeconds(stretch.end_time)) {
			return;
		}

		onChange({ ...stretch, start_time });
	}

	function handleEndChange(value: Moment | null): void {
		if (!value) {
			return;
		}

		const end_time = toDayjs(value).millisecond(LAST_MILLISECOND);

		if (dayjsToSeconds(end_time) <= dayjsToSeconds(stretch.start_time)) {
			return;
		}

		onChange({ ...stretch, end_time });
	}

	function handleRemove(): void {
		onRemove(stretch.id);
	}

	return (
		<StretchRow ref={rowRef}>
			<StretchIndex>{position}</StretchIndex>
			<StretchInterval>
				<TimePicker
					allowClear={false}
					format={TIME_FORMAT}
					suffixIcon={<ClockIcon />}
					value={startValue}
					disabledTime={getPickerBounds}
					placement={placement}
					onOpenChange={handleOpenChange}
					onChange={handleStartChange}
				/>
				<StretchArrow width={16} height={16} />
				<TimePicker
					allowClear={false}
					format={TIME_FORMAT}
					suffixIcon={<ClockIcon />}
					value={endValue}
					disabledTime={getPickerBounds}
					placement={placement}
					onOpenChange={handleOpenChange}
					onChange={handleEndChange}
				/>
			</StretchInterval>
			<StretchDuration>{`${I18n.get('Duration')}: ${duration} ${I18n.get('seconds')}`}</StretchDuration>
			{!!fileName && <StretchFileName>{fileName}</StretchFileName>}
			<RemoveStretchButton
				type="text"
				icon={<MinusIcon />}
				aria-label={I18n.get('Remove stretch')}
				onClick={handleRemove}
			/>
		</StretchRow>
	);
}
